import { getNow } from "@/utils/dateService";

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export type TRelativeTime = {
    /** Relative phrase for display, or the absolute form when relative does not apply. */
    label: string;
    /** Absolute local form, for a `title` tooltip. */
    full: string;
};

/**
 * Age of a timestamp as a short relative phrase.
 *
 * The clock comes from `getNow()` rather than `Date.now()` so tests can freeze it.
 *
 * `maxRelativeMs` caps how old a timestamp may be before the phrase gives up and
 * shows the absolute form instead — omit it for an uncapped count of days, which
 * is what the review header's "Submitted X days ago" needs. A future or
 * unparseable timestamp never gets a relative phrase.
 */
export function formatRelativeTime(
    iso: string,
    options?: { maxRelativeMs?: number },
): TRelativeTime {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return { label: "", full: "" };

    const full = date.toLocaleString();
    const diff = getNow().getTime() - date.getTime();
    const maxRelativeMs = options?.maxRelativeMs;

    if (diff < 0 || (maxRelativeMs !== undefined && diff > maxRelativeMs)) {
        return { label: full, full };
    }
    if (diff < MINUTE_MS) return { label: "just now", full };
    if (diff < HOUR_MS) return { label: `${Math.floor(diff / MINUTE_MS)}m ago`, full };
    if (diff < DAY_MS) return { label: `${Math.floor(diff / HOUR_MS)}h ago`, full };
    return { label: `${Math.floor(diff / DAY_MS)}d ago`, full };
}
